// PostHog (client) bootstrap — unifies anonymous browser sessions with the
// server-side distinct_ids. Every call is a graceful no-op until init runs and
// never throws, so analytics can never block a core flow.
//
// PERF: The ~164 KB raw SDK is dynamically imported inside `initPostHog()`
// so it stays out of `main.js`. `index.js` calls `initPostHog()` on the
// `load` event via requestIdleCallback (with a setTimeout fallback).
//
// Any `phCapture` / `phIdentify` / `phReset` calls fired *before* the SDK
// finishes loading are buffered here and flushed once posthog reports itself
// loaded — so an immediate landing-CTA click still lands as an event.

let _posthog = null;      // resolved SDK module (after dynamic import)
let _ready = false;       // true once `loaded` callback fires
let _initStarted = false; // dedupe initPostHog() calls
const MAX_QUEUE = 200;    // hard cap so a broken/never-inits config can't grow forever
const _queue = [];

function _flush() {
  if (!_posthog) return;
  while (_queue.length) {
    const item = _queue.shift();
    try { _posthog[item.method](...item.args); } catch { /* ignore */ }
  }
}

function _enqueueOrCall(method, args) {
  if (_ready && _posthog) {
    try { _posthog[method](...args); } catch { /* ignore */ }
    return;
  }
  if (_queue.length >= MAX_QUEUE) return;
  _queue.push({ method, args });
}

export function initPostHog({ apiKey, apiHost } = {}) {
  if (_initStarted) return;
  _initStarted = true;
  if (!apiKey) return; // not configured — stay disabled; queued calls are dropped
  // Dynamic import — the SDK ships as its own chunk, loaded lazily.
  import(/* webpackChunkName: "posthog" */ 'posthog-js').then(({ default: posthog }) => {
    _posthog = posthog;
    try {
      posthog.init(apiKey, {
        api_host: apiHost || 'https://us.i.posthog.com',
        capture_pageview: true,
        autocapture: false,
        persistence: 'localStorage+cookie',
        // Privacy: never record raw inputs/text (questionnaire answers, PII).
        mask_all_text: true,
        mask_all_element_attributes: true,
        loaded: () => {
          _ready = true;
          _flush();
        },
      });
      // Expose the SDK on window so DevTools, integration probes, and any
      // legacy code expecting the classic snippet-installed global find it.
      // The queue-shim used by our exports (phCapture/phIdentify) still runs
      // through `_posthog` — this assignment is purely for external
      // consumers (matches PostHog's own snippet behaviour).
      try {
        if (typeof window !== 'undefined') {
          window.posthog = posthog;
        }
      } catch { /* ignore */ }
    } catch {
      /* never break the app */
    }
  }).catch(() => {
    /* SDK chunk failed to load — stay disabled, queue is dropped */
  });
}

// Public API — safe to call before init; calls are buffered and flushed
// automatically once posthog reports loaded.
export function phCapture(event, properties) {
  if (!event) return;
  _enqueueOrCall('capture', properties ? [event, properties] : [event]);
}

export function phIdentify(user) {
  if (!user) return;
  const id = user.id || user.email;
  if (!id) return;
  _enqueueOrCall('identify', [id, { email: user.email || undefined }]);
}

export function phReset() {
  _enqueueOrCall('reset', []);
}

// Test hook — inspect queue depth in tests. Not for production use.
export function _phQueueLengthForTesting() {
  return _queue.length;
}
